import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingContactIcons, { DownloadCatalogModal } from '../../components/feature/FloatingContactIcons';
import { useContentManager } from '../../hooks/useContentManager';
import useSiteSettings from '../../hooks/useSiteSettings';

// New Brand Sections based on client specification
import BrandAbout from './components/BrandAbout';
import BrandResidences from './components/BrandResidences';
import BrandLocation from './components/BrandLocation';
import BrandAmenities from './components/BrandAmenities';
import BrandInvestmentForm from './components/BrandInvestmentForm';

export default function BrandPage() {
  const { getContentItem } = useContentManager();
  const siteSettings = useSiteSettings();
  const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);

  // 1. Hero Content (admin-managed)
  const heroVideo = getContentItem('brand', 'hero-video');
  const heroVideoUrl = heroVideo?.video_url || heroVideo?.content || '';

  const brandHeroText = getContentItem('brand', 'hero-text');
  const brandHeroLogo =
    brandHeroText?.metadata?.logo_url ||
    siteSettings?.organization_logo ||
    'https://cdn.berkongroup.com/media/images/png%20logo%20golden_1765904823509.webp';
  const brandHeroTitleLine1 = brandHeroText?.metadata?.title_line1 || 'Prestigious Address,';
  const brandHeroTitleBold = brandHeroText?.metadata?.title_bold || 'Prestigious Living';
  const brandHeroSubtitle = brandHeroText?.metadata?.subtitle || '';
  const brandHeroButtonText = (brandHeroText?.metadata?.button_text as string | undefined) || 'DOWNLOAD CATALOGUE';
  const brandHeroButtonUrl = brandHeroText?.metadata?.button_url as string | undefined;
  const brandHeroButton2Text = (brandHeroText?.metadata?.button2_text as string | undefined) || 'EXPLORE RESIDENCES';
  const brandHeroButton2Url = (brandHeroText?.metadata?.button2_url as string | undefined) || '#curated-residences';

  // Catalog URL
  const catalogPdfUrl = (brandHeroButtonUrl && brandHeroButtonUrl.endsWith('.pdf'))
    ? brandHeroButtonUrl
    : (siteSettings?.pdf_url || '/catalog.pdf');

  const isCatalogAction = (text?: string, url?: string) => {
    if (!text) return false;
    const t = text.toLowerCase();
    const u = (url || '').trim().toLowerCase();
    return (
      t.includes('catalog') ||
      t.includes('brochure') ||
      t.includes('download') ||
      t.includes('katalog') ||
      t.includes('broşür') ||
      u === '' ||
      u === '#' ||
      u.endsWith('.pdf')
    );
  };

  // Section data from admin (or fallback to defaults)
  const aboutData = getContentItem('brand', 'about')?.metadata;
  const residencesData = getContentItem('brand', 'residences')?.metadata;
  const locationData = getContentItem('brand', 'location')?.metadata || getContentItem('brand', 'location-surroundings')?.metadata;
  const amenitiesData = getContentItem('brand', 'amenities')?.metadata;
  const investmentData = getContentItem('brand', 'investment')?.metadata;

  return (
    <div className="min-h-screen bg-black text-white selection:bg-[#C5A46D] selection:text-black">
      <Header />

      {/* ── 1. HERO SECTION ────────────────────────────────────────── */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0 z-0">
          <video 
            autoPlay 
            muted 
            loop 
            playsInline 
            className="w-full h-full object-cover"
            key={heroVideoUrl}
          >
            <source src={heroVideoUrl || 'https://assets.mixkit.co/videos/preview/mixkit-modern-apartment-building-at-sunset-41372-large.mp4'} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80"></div>
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 flex flex-col justify-center items-center h-full text-center px-4">
          <div className="space-y-4 max-w-5xl mx-auto">
            {/* Golden Tower Dedicated Logo */}
            {brandHeroLogo && (
              <div className="mb-4 sm:mb-6 flex justify-center">
                <img
                  src={brandHeroLogo}
                  alt="Golden Tower"
                  className="h-28 sm:h-32 md:h-40 lg:h-44 w-auto max-w-[340px] sm:max-w-[440px] md:max-w-[520px] object-contain filter drop-shadow-[0_4px_28px_rgba(0,0,0,0.9)] transition-all duration-300"
                />
              </div>
            )}

            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-light tracking-wide text-white mb-4 leading-tight">
              {brandHeroTitleLine1} <span className="font-bold">{brandHeroTitleBold}</span>
            </h1>
            {brandHeroSubtitle?.trim() && (
              <p className="max-w-3xl mx-auto text-base md:text-lg text-white/80 mb-2">{brandHeroSubtitle}</p>
            )}

            {(brandHeroButtonText?.trim() || brandHeroButton2Text?.trim()) && (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6">
                {/* 1. Button (Download Catalogue) */}
                {brandHeroButtonText?.trim() && (
                  isCatalogAction(brandHeroButtonText, brandHeroButtonUrl) ? (
                    <button
                      onClick={() => setIsCatalogModalOpen(true)}
                      className="px-8 py-3.5 text-xs md:text-sm tracking-[0.2em] uppercase font-medium border border-white/80 text-white hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap backdrop-blur-xs"
                    >
                      {brandHeroButtonText.trim()}
                    </button>
                  ) : (
                    <a
                      href={brandHeroButtonUrl}
                      target={brandHeroButtonUrl?.startsWith('http') ? '_blank' : undefined}
                      rel={brandHeroButtonUrl?.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className="px-8 py-3.5 text-xs md:text-sm tracking-[0.2em] uppercase font-medium border border-white/80 text-white hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap backdrop-blur-xs"
                    >
                      {brandHeroButtonText.trim()}
                    </a>
                  )
                )}

                {/* 2. Button (Explore Residences -> Curated Residences Section) */}
                {brandHeroButton2Text?.trim() && (() => {
                  const targetAnchor = (brandHeroButton2Url && brandHeroButton2Url !== '#' && brandHeroButton2Url.startsWith('#'))
                    ? brandHeroButton2Url
                    : '#curated-residences';

                  return (
                    <a
                      href={targetAnchor}
                      onClick={(e) => {
                        e.preventDefault();
                        const targetId = targetAnchor.replace('#', '');
                        const el = document.getElementById(targetId) || document.getElementById('curated-residences') || document.getElementById('residences');
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className="px-8 py-3.5 text-xs md:text-sm tracking-[0.2em] uppercase font-medium border border-[#C5A46D] bg-[#C5A46D] text-black hover:bg-transparent hover:text-[#C5A46D] transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg shadow-[#C5A46D]/20"
                    >
                      {brandHeroButton2Text.trim()}
                    </a>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── 2. ABOUT SECTION ───────────────────────────────────────── */}
      <BrandAbout data={aboutData} />

      {/* ── 3. RESIDENCE SECTION ───────────────────────────────────── */}
      <BrandResidences
        data={residencesData}
        onDownloadCatalogue={() => setIsCatalogModalOpen(true)}
      />

      {/* ── 4. LOCATION SECTION ────────────────────────────────────── */}
      <BrandLocation data={locationData} />

      {/* ── 5. AMENITIES SECTION ───────────────────────────────────── */}
      <BrandAmenities data={amenitiesData} />

      {/* ── 6. & 7. INVESTMENT OPPORTUNITIES & FORM ───────────────── */}
      <BrandInvestmentForm
        data={investmentData}
        onDownloadGuide={() => setIsCatalogModalOpen(true)}
      />

      {/* ── FOOTER & FLOATING ICONS ─────────────────────────────────── */}
      <Footer />
      <FloatingContactIcons />

      {/* Download Catalog Modal */}
      <DownloadCatalogModal
        isOpen={isCatalogModalOpen}
        onClose={() => setIsCatalogModalOpen(false)}
        pdfUrl={catalogPdfUrl}
      />
    </div>
  );
}
